// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ConnectedApplication } from '@/account/api/get-connected-applications';
import { useSignOutConnectedApplicationSession } from '@/account/hooks/use-sign-out-connected-application-session';
import { toPermissionLabel } from '@/account/utilities/to-permission-label';
import {
  toSessionSubtitle,
  toSessionTitle,
} from '@/account/utilities/to-session-details';
import { FormCardRow } from '@/components/form-card-row';
import { ExpandMoreRounded } from '@/constants/hardfin-standard-icons';
import {
  Button,
  ButtonBase,
  Collapse,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { Fragment, useState } from 'react';

interface Props {
  application: ConnectedApplication;
  onDisconnect: () => void;
}

/** Shows one connected application, and the sessions it can be signed out of. */
export function ConnectedApplicationRow(props: Props) {
  const [isManagingSessions, setIsManagingSessions] = useState(false);
  const signOut = useSignOutConnectedApplicationSession();

  return (
    <>
      <Stack
        useFlexGap
        direction={{ xs: 'column', md: 'row' }}
        padding={2}
        spacing={2}
        justifyContent="space-between"
      >
        <ButtonBase
          aria-expanded={isManagingSessions}
          onClick={() => setIsManagingSessions(!isManagingSessions)}
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: 1,
            textAlign: 'left',
            borderRadius: 1,
          }}
        >
          <ExpandMoreRounded
            fontSize="small"
            color="action"
            sx={{
              transform: isManagingSessions ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s',
            }}
          />
          <Stack>
            <Typography variant="subtitle2">
              {props.application.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`${toPermissionLabel(props.application.scopes)} · Connected ${props.application.connectedAt.toLocaleString(DateTime.DATE_MED)}`}
            </Typography>
          </Stack>
        </ButtonBase>
        <Button
          size="small"
          color="error"
          variant="outlined"
          onClick={props.onDisconnect}
          sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
        >
          Disconnect
        </Button>
      </Stack>
      <Collapse in={isManagingSessions} unmountOnExit>
        <Divider />
        <Stack bgcolor="grey.50" paddingLeft={3.5}>
          {props.application.sessions.length === 0 && (
            <FormCardRow
              title="No active sessions"
              subtitle={`${props.application.name} is signed out everywhere`}
            />
          )}
          {props.application.sessions.map((session, index) => (
            <Fragment key={session.id}>
              {index > 0 && <Divider />}
              <FormCardRow
                title={toSessionTitle(session)}
                subtitle={toSessionSubtitle(session)}
              >
                <Button
                  size="small"
                  variant="outlined"
                  disabled={signOut.isPending}
                  onClick={() =>
                    signOut.mutate({
                      applicationKey: props.application.key,
                      sessionId: session.id,
                    })
                  }
                  sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
                >
                  Sign out
                </Button>
              </FormCardRow>
            </Fragment>
          ))}
        </Stack>
      </Collapse>
    </>
  );
}
